import { useState, useEffect } from "react";
import { db } from "./firebase";
import {
  collection, addDoc, getDocs, deleteDoc, doc, updateDoc,
  orderBy, query, serverTimestamp
} from "firebase/firestore";

// ─── CONFIGURATION ────────────────────────────────────────────────────────────
const ADMIN_PASSWORD = "ikon2019";
const ADMIN_EMAIL = "tracy@ikonlandscape.com";
const SUPERVISOR_EMAILS = "tracy@ikonlandscape.com, kaylie@ikonlandscape.com";
const APP_URL = "https://www.ikonlandscape.com/action-log";
const EMAILJS_SERVICE_ID = "service_otexltu";
const EMAILJS_TEMPLATE_ID = "template_a9qtd0n";
const EMAILJS_PUBLIC_KEY = "zoBrnWPbYuR6eAuRP";
// ─────────────────────────────────────────────────────────────────────────────

const BRAND = {
  black:"#010101", green:"#424b33", white:"#fefefe",
  lightGray:"#e3e4e3", gray:"#a5a7a9", greenDark:"#2f3626", greenLight:"#535f42",
};

const INCIDENT_TYPES = {
  REC:  { label:"Recognition",        color:"#424b33", bg:"#f2f4ee", border:"#c8cebc", badge:"#dde2d0", items:["Going Above & Beyond","Customer Compliment","Core Value Demonstrated","Leadership / Mentorship","Safety Excellence","Equipment Care & Responsibility"] },
  SAFE: { label:"Safety",             color:"#7a1e1e", bg:"#fdf2f2", border:"#f5c0c0", badge:"#fbd9d9", items:["PPE Violation","Unsafe Equipment Operation","Near Miss","Workplace Injury","Failure to Follow Safety Procedure","Jobsite Hazard Not Reported","Client Property Damage","Drug or Alcohol Policy Violation"] },
  ATT:  { label:"Attendance",         color:"#7a5a1e", bg:"#fdf8ee", border:"#f0dba0", badge:"#f7eccc", items:["Late to Work","No Call / No Show","Left Jobsite Without Approval","Missed Scheduled Shift"] },
  PERF: { label:"Performance",        color:"#4a3070", bg:"#f5f2fb", border:"#cfc4ed", badge:"#e4ddf5", items:["Poor Workmanship","Rework Required","Failure to Follow Job Scope","Productivity Below Expectations"] },
  COND: { label:"Conduct",            color:"#7a1e50", bg:"#fdf2f7", border:"#f0b8d4", badge:"#f7d4e5", items:["Unprofessional Conduct","Disrespectful Communication","Insubordination","Conflict with Coworker","Conflict with Customer"] },
  EQP:  { label:"Equipment / Vehicle",color:"#1a4a6e", bg:"#f0f6fb", border:"#b8d4ed", badge:"#d4e8f7", items:["Equipment Damage","Vehicle Accident","Failure to Perform Equipment Inspection","Lost or Misplaced Tools"] },
  ADMIN:{ label:"Administrative",     color:"#1a5a50", bg:"#f0faf8", border:"#a0d8d0", badge:"#c8ecea", items:["SOP Not Followed","Failure to Complete Training","Incomplete Paperwork","Incorrect Time Entry"] },
};

const SEVERITY = ["Low","Medium","High","Critical"];
const ACTION_TAKEN = ["Verbal Warning","Written Warning","Final Written Warning","Suspension","Termination","Coaching / Counseling","Recognition / Reward","No Action Required","Training Required"];
const font = "'Cambria', 'Georgia', serif";

const sel = {
  width:"100%",
  padding:"9px 12px",
  borderRadius:6,
  border:`1.5px solid ${BRAND.lightGray}`,
  fontSize:13,
  color:BRAND.black,
  background:BRAND.white,
  outline:"none",
  boxSizing:"border-box",
  fontFamily:font,
};

const initialForm = {
  date:new Date().toISOString().split("T")[0],
  employeeName:"",
  employeeId:"",
  supervisor:"",
  division:"",
  jobsite:"",
  category:"",
  subType:"",
  severity:"",
  description:"",
  actionTaken:"",
  witnesses:"",
  followUpDate:"",
  aspireTicket:"",
  supervisorEmail:""
};

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 767);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return isMobile;
}

function grid(cols, gap=16) {
  return {
    display:"grid",
    gridTemplateColumns:`repeat(${cols}, minmax(0, 1fr))`,
    gap
  };
}

const sendEmailNotification = async (action) => {
  try {
    const res = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
      method:"POST",
      headers:{"Content-Type":"application/json"},
      body: JSON.stringify({
        service_id:EMAILJS_SERVICE_ID,
        template_id:EMAILJS_TEMPLATE_ID,
        user_id:EMAILJS_PUBLIC_KEY,
        template_params: {
          to_email:ADMIN_EMAIL,
          employee_name:action.employeeName,
          action_code:action.code,
          category:action.categoryLabel,
          description:action.description,
          action_taken:action.actionTaken || "Not specified",
          action_date:action.date,
          supervisor:action.supervisor || "Not specified",
          supervisor_email:action.supervisorEmail || SUPERVISOR_EMAILS,
          app_url:APP_URL,
        },
      }),
    });
    return res.status===200;
  } catch(e) {
    console.error("Email error:",e);
    return false;
  }
};

function Field({ label, placeholder, value, onChange, type="text", inputStyle={} }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label style={{ display:"block", fontSize:13, fontWeight:600, color:BRAND.black, marginBottom:6, fontFamily:font }}>
        {label}
      </label>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={e=>onChange(e.target.value)}
        style={sel}
      />
    </div>
  );
}

function Tag({ label }) {
  return (
    <span style={{ background:BRAND.lightGray, border:`1px solid ${BRAND.gray}`, borderRadius:4, padding:"2px 8px", fontSize:11, color:BRAND.black, fontFamily:font }}>
      {label}
    </span>
  );
}

function IkonLogo() {
  return (
    <img
      src="https://images.squarespace-cdn.com/content/v1/5c46402ec3c16a9eac57e322/1552164399766-2E1EW479SHFFX32IOT8T/MainLogoGrayLandscape.png?format=1500w"
      alt="IKON Landscape"
      style={{ height:48, width:"auto", maxWidth:"100%" }}
    />
  );
}

function PrintModal({ action, onClose }) {
  const cat = INCIDENT_TYPES[action.category];

  const handlePrint = () => {
    const content = document.getElementById("print-content").innerHTML;
    const win = window.open("","_blank");
    win.document.write(`
      <html>
        <head>
          <title>IKON – ${action.employeeName}</title>
          <style>
            body{font-family:Cambria,Georgia,serif;color:#010101;margin:0;padding:32px;font-size:13px}
            .g3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px 24px}
            .fv{font-weight:600;font-size:13px;border-bottom:1px solid #e3e4e3;padding-bottom:4px;min-height:20px}
            .fl{font-size:11px;color:#a5a7a9;margin-bottom:2px}
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(()=>win.print(),400);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ background:BRAND.white, borderRadius:12, width:"100%", maxWidth:700, maxHeight:"90vh", overflow:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.4)" }}>
        <div style={{ padding:"16px 20px", borderBottom:`1px solid ${BRAND.lightGray}`, display:"flex", justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, background:BRAND.white, gap:10, flexWrap:"wrap" }}>
          <span style={{ fontWeight:700, fontSize:15, color:BRAND.black, fontFamily:font }}>
            Print Preview – {action.employeeName}
          </span>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <button onClick={handlePrint} style={{ padding:"8px 18px", borderRadius:6, border:"none", background:BRAND.green, color:BRAND.white, fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:font }}>
              🖨 Print / Save PDF
            </button>
            <button onClick={onClose} style={{ padding:"8px 14px", borderRadius:6, border:`1px solid ${BRAND.lightGray}`, background:BRAND.white, fontWeight:600, fontSize:13, cursor:"pointer", fontFamily:font }}>
              ✕ Close
            </button>
          </div>
        </div>

        <div id="print-content" style={{ padding:"28px 32px", fontFamily:font }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", borderBottom:`3px solid ${BRAND.black}`, paddingBottom:16, marginBottom:24, gap:12, flexWrap:"wrap" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
              <img
                src="https://images.squarespace-cdn.com/content/v1/5c46402ec3c16a9eac57e322/1552164399766-2E1EW479SHFFX32IOT8T/MainLogoGrayLandscape.png?format=1500w"
                alt="IKON Landscape"
                style={{ height:50, width:"auto" }}
              />
              <div style={{ fontSize:11, color:BRAND.gray, letterSpacing:1, marginLeft:8 }}>
                Action & Recognition Report
              </div>
            </div>

            <div style={{ textAlign:"right" }}>
              <div style={{ display:"inline-block", padding:"4px 12px", borderRadius:5, fontWeight:700, fontSize:12, background:cat.badge, color:cat.color, marginBottom:4 }}>
                {action.category} – {cat.label}
              </div>
              <div style={{ fontSize:11, color:BRAND.gray }}>Date: {action.date}</div>
            </div>
          </div>

          {action.category==="REC" && (
            <div style={{ background:"#f2f4ee", border:`2px solid ${BRAND.green}`, borderRadius:8, padding:"10px 14px", color:BRAND.green, fontWeight:700, marginBottom:16, fontSize:13 }}>
              ⭐ RECOGNITION RECORD — Positive performance acknowledgment.
            </div>
          )}

          <div style={{ fontSize:11, fontWeight:700, color:BRAND.gray, textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>
            Employee Information
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"10px 24px", marginBottom:20 }}>
            {[
              ["Employee Name",action.employeeName],
              ["Employee ID",action.employeeId||"—"],
              ["Supervisor",action.supervisor],
              ["Division",action.division||"—"],
              ["Jobsite",action.jobsite||"—"],
              ["Aspire Ticket",action.aspireTicket||"—"]
            ].map(([l,v])=>(
              <div key={l}>
                <div style={{ fontSize:11, color:BRAND.gray, marginBottom:2 }}>{l}</div>
                <div style={{ fontWeight:600, fontSize:13, borderBottom:`1px solid ${BRAND.lightGray}`, paddingBottom:4, minHeight:20 }}>{v}</div>
              </div>
            ))}
          </div>

          <div style={{ fontSize:11, fontWeight:700, color:BRAND.gray, textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>
            Action Details
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"10px 24px", marginBottom:20 }}>
            {[
              ["Code",action.code],
              ["Severity",action.severity||"—"],
              ["Action Taken",action.actionTaken||"—"],
              ["Witnesses",action.witnesses||"—"],
              ["Follow-Up",action.followUpDate||"—"]
            ].map(([l,v])=>(
              <div key={l}>
                <div style={{ fontSize:11, color:BRAND.gray, marginBottom:2 }}>{l}</div>
                <div style={{ fontWeight:600, fontSize:13, borderBottom:`1px solid ${BRAND.lightGray}`, paddingBottom:4, minHeight:20 }}>{v}</div>
              </div>
            ))}
          </div>

          <div style={{ fontSize:11, fontWeight:700, color:BRAND.gray, textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>
            Description
          </div>

          <div style={{ border:`1px solid ${BRAND.lightGray}`, borderRadius:6, padding:12, minHeight:80, lineHeight:1.7, fontSize:13, marginBottom:24 }}>
            {action.description}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:24 }}>
            {[
              ["Employee Signature",action.employeeSignature],
              ["Supervisor Signature",action.supervisorSignature],
              ["Date",""]
            ].map(([l,v])=>(
              <div key={l}>
                <div style={{ borderBottom:`1.5px solid ${BRAND.black}`, minHeight:40, paddingBottom:4, fontSize:13 }}>{v||""}</div>
                <div style={{ fontSize:11, color:BRAND.gray, marginTop:4 }}>{l}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop:40, borderTop:`1px solid ${BRAND.lightGray}`, paddingTop:12, display:"flex", justifyContent:"space-between", fontSize:10, color:BRAND.gray }}>
            <span>IKON Landscape — Action Logger</span>
            <span>Confidential — Internal Use Only</span>
            <span>Generated: {new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminLogin({ onLogin }) {
  const [pw,setPw]=useState("");
  const [err,setErr]=useState(false);

  const attempt = () => pw===ADMIN_PASSWORD ? onLogin() : setErr(true);

  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"60vh" }}>
      <div style={{ background:BRAND.white, borderRadius:12, border:`1px solid ${BRAND.lightGray}`, padding:"36px 40px", maxWidth:340, width:"100%", textAlign:"center", boxShadow:"0 4px 20px rgba(0,0,0,0.1)" }}>
        <div style={{ fontSize:32, marginBottom:8 }}>🔒</div>
        <div style={{ fontWeight:800, fontSize:18, color:BRAND.black, marginBottom:4, fontFamily:font }}>Admin Access</div>
        <div style={{ fontSize:13, color:BRAND.gray, marginBottom:20, fontFamily:font }}>
          Enter the admin password to manage records and view the dashboard.
        </div>
        <input
          type="password"
          placeholder="Password"
          value={pw}
          onChange={e=>{setPw(e.target.value);setErr(false);}}
          onKeyDown={e=>e.key==="Enter"&&attempt()}
          style={{ ...sel, marginBottom:10, textAlign:"center", fontSize:16 }}
        />
        {err && <div style={{ color:"#7a1e1e", fontSize:12, marginBottom:8, fontFamily:font }}>Incorrect password.</div>}
        <button onClick={attempt} style={{ width:"100%", padding:"11px", borderRadius:7, border:"none", background:BRAND.green, color:BRAND.white, fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:font }}>
          Unlock Admin
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const mobile = useIsMobile();

  const [view,setView]=useState("log");
  const [form,setForm]=useState(initialForm);
  const [actions,setActions]=useState([]);
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [submitted,setSubmitted]=useState(false);
  const [filterCat,setFilterCat]=useState("ALL");
  const [search,setSearch]=useState("");
  const [printInc,setPrintInc]=useState(null);
  const [isAdmin,setIsAdmin]=useState(false);
  const [editInc,setEditInc]=useState(null);
  const [deleteId,setDeleteId]=useState(null);

  useEffect(() => {
    const sendHeight = () => {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage(
        {
          type: "ikonActionLoggerHeight",
          height,
        },
        "*"
      );
    };

    sendHeight();

    let resizeObserver;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        sendHeight();
      });
      resizeObserver.observe(document.body);
    }

    window.addEventListener("load", sendHeight);
    window.addEventListener("resize", sendHeight);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("load", sendHeight);
      window.removeEventListener("resize", sendHeight);
    };
  }, [view, form, actions, loading, submitted, editInc, deleteId, mobile]);

  const loadActions = async () => {
    setLoading(true);
    try {
      const q = query(collection(db,"incidents"), orderBy("createdAt","desc"));
      const snap = await getDocs(q);
      setActions(snap.docs.map(d=>({...d.data(), firestoreId:d.id})));
    } catch(e) {
      console.error("Load error:",e);
    }
    setLoading(false);
  };

  useEffect(()=>{ loadActions(); },[]);

  const handleSubmit = async () => {
    if (!form.employeeName || !form.category || !form.subType || !form.description) {
      alert("Please fill in all required fields.");
      return;
    }

    setSaving(true);
    try {
      const cat = INCIDENT_TYPES[form.category];
      const actionData = {
        ...form,
        code:`${form.category} – ${form.subType}`,
        categoryLabel:cat.label,
        createdAt:serverTimestamp()
      };

      await addDoc(collection(db,"incidents"), actionData);
      await sendEmailNotification(actionData);
      setSubmitted(true);
      setForm(initialForm);
      await loadActions();
      setTimeout(()=>setSubmitted(false),3000);
    } catch(e) {
      alert("Error saving. Check your Firebase connection.");
      console.error(e);
    }
    setSaving(false);
  };

  const handleDelete = async (firestoreId) => {
    try {
      await deleteDoc(doc(db,"incidents",firestoreId));
      setDeleteId(null);
      await loadActions();
    } catch(e) {
      alert("Error deleting.");
    }
  };

  const handleEdit = async () => {
    if (!editInc) return;

    try {
      const cat = INCIDENT_TYPES[editInc.category];
      const { firestoreId, ...data } = editInc;
      await updateDoc(doc(db,"incidents",firestoreId), {
        ...data,
        code:`${editInc.category} – ${editInc.subType}`,
        categoryLabel:cat.label
      });
      setEditInc(null);
      await loadActions();
    } catch(e) {
      alert("Error saving.");
    }
  };

  const recCount  = actions.filter(i=>i.category==="REC").length;
  const corrCount = actions.filter(i=>i.category!=="REC").length;
  const ratio     = corrCount===0 ? "∞" : (recCount/corrCount).toFixed(1);
  const ratioGood = corrCount===0 || recCount/corrCount>=2;
  const catCounts = Object.keys(INCIDENT_TYPES).reduce((acc,k)=>{
    acc[k]=actions.filter(i=>i.category===k).length;
    return acc;
  },{});

  const filtered = actions.filter(i=>{
    const matchCat = filterCat==="ALL" || i.category===filterCat;
    const matchSearch =
      !search ||
      i.employeeName?.toLowerCase().includes(search.toLowerCase()) ||
      i.code?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div
      style={{
        fontFamily:font,
        minHeight:"100vh",
        background:"#f5f5f3",
        color:BRAND.black,
        width:"100%",
        maxWidth:"100%",
        overflowX:"hidden",
      }}
    >
      {printInc && <PrintModal action={printInc} onClose={()=>setPrintInc(null)} />}

      {deleteId && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:998, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
          <div style={{ background:BRAND.white, borderRadius:12, padding:"28px 32px", maxWidth:360, width:"100%", textAlign:"center", boxShadow:"0 10px 40px rgba(0,0,0,0.2)", fontFamily:font }}>
            <div style={{ fontSize:28, marginBottom:8 }}>🗑</div>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:8 }}>Delete this record?</div>
            <div style={{ color:BRAND.gray, fontSize:13, marginBottom:20 }}>This cannot be undone.</div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={()=>setDeleteId(null)} style={{ flex:1, padding:"10px", borderRadius:7, border:`1px solid ${BRAND.lightGray}`, background:BRAND.white, fontWeight:600, cursor:"pointer", fontFamily:font }}>
                Cancel
              </button>
              <button onClick={()=>handleDelete(deleteId)} style={{ flex:1, padding:"10px", borderRadius:7, border:"none", background:"#7a1e1e", color:BRAND.white, fontWeight:700, cursor:"pointer", fontFamily:font }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {editInc && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:998, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
          <div style={{ background:BRAND.white, borderRadius:12, width:"100%", maxWidth:600, maxHeight:"90vh", overflow:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ padding:"16px 20px", borderBottom:`1px solid ${BRAND.lightGray}`, display:"flex", justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, background:BRAND.white, gap:10, flexWrap:"wrap" }}>
              <span style={{ fontWeight:700, fontSize:15, color:BRAND.black, fontFamily:font }}>
                Edit Record – {editInc.employeeName}
              </span>
              <button onClick={()=>setEditInc(null)} style={{ padding:"6px 12px", borderRadius:6, border:`1px solid ${BRAND.lightGray}`, background:BRAND.white, cursor:"pointer", fontFamily:font }}>
                ✕
              </button>
            </div>

            <div style={{ padding:24, display:"flex", flexDirection:"column", gap:14 }}>
              <div style={grid(mobile ? 1 : 2)}>
                <Field label="Employee Name" value={editInc.employeeName} onChange={v=>setEditInc({...editInc,employeeName:v})} />
                <Field label="Date" type="date" value={editInc.date} onChange={v=>setEditInc({...editInc,date:v})} />
                <Field label="Supervisor" value={editInc.supervisor} onChange={v=>setEditInc({...editInc,supervisor:v})} />
                <Field label="Division" value={editInc.division||""} onChange={v=>setEditInc({...editInc,division:v})} />
                <Field label="Jobsite" value={editInc.jobsite||""} onChange={v=>setEditInc({...editInc,jobsite:v})} />
                <Field label="Aspire Ticket" value={editInc.aspireTicket||""} onChange={v=>setEditInc({...editInc,aspireTicket:v})} />
              </div>

              <div>
                <label style={{ display:"block", fontSize:13, fontWeight:600, color:BRAND.black, marginBottom:6, fontFamily:font }}>Severity</label>
                <select value={editInc.severity||""} onChange={e=>setEditInc({...editInc,severity:e.target.value})} style={sel}>
                  <option value="">—</option>
                  {SEVERITY.map(s=><option key={s}>{s}</option>)}
                </select>
              </div>

              <div>
                <label style={{ display:"block", fontSize:13, fontWeight:600, color:BRAND.black, marginBottom:6, fontFamily:font }}>Action Taken</label>
                <select value={editInc.actionTaken||""} onChange={e=>setEditInc({...editInc,actionTaken:e.target.value})} style={sel}>
                  <option value="">—</option>
                  {ACTION_TAKEN.map(a=><option key={a}>{a}</option>)}
                </select>
              </div>

              <div>
                <label style={{ display:"block", fontSize:13, fontWeight:600, color:BRAND.black, marginBottom:6, fontFamily:font }}>Description</label>
                <textarea value={editInc.description} onChange={e=>setEditInc({...editInc,description:e.target.value})} rows={4} style={{ ...sel, resize:"vertical" }} />
              </div>

              <div style={grid(mobile ? 1 : 2)}>
                <Field label="Witnesses" value={editInc.witnesses||""} onChange={v=>setEditInc({...editInc,witnesses:v})} />
                <Field label="Follow-Up Date" type="date" value={editInc.followUpDate||""} onChange={v=>setEditInc({...editInc,followUpDate:v})} />
              </div>

              <button onClick={handleEdit} style={{ padding:"12px", borderRadius:7, border:"none", background:BRAND.green, color:BRAND.white, fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:font }}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          background: BRAND.black,
          padding: mobile ? "10px 12px" : "14px 28px",
          display: "flex",
          alignItems: mobile ? "flex-start" : "center",
          justifyContent: "space-between",
          flexDirection: mobile ? "column" : "row",
          gap: mobile ? 10 : 0,
          boxShadow: "0 2px 12px rgba(0,0,0,0.4)",
          borderBottom: `3px solid ${BRAND.green}`,
        }}
      >
        <IkonLogo />

        <div style={{ display:"flex", gap:6, width: mobile ? "100%" : "auto", flexWrap:"wrap" }}>
          {[["log","＋ Log"],["admin",isAdmin?"⚙ Admin":"🔒 Admin"]].map(([v,label])=>(
            <button
              key={v}
              onClick={()=>setView(v)}
              style={{
                padding: mobile ? "8px 12px" : "8px 16px",
                borderRadius:5,
                border:view===v ? `1.5px solid ${BRAND.green}` : `1.5px solid rgba(255,255,255,0.15)`,
                cursor:"pointer",
                fontWeight:600,
                fontSize: mobile ? 12 : 13,
                background:view===v ? BRAND.green : "transparent",
                color:BRAND.white,
                fontFamily:font,
                letterSpacing:0.5,
                transition:"all 0.15s",
                flex: mobile ? 1 : "none",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div
        style={{
          background: BRAND.green,
          padding: mobile ? "8px 12px" : "8px 28px",
          display: "flex",
          alignItems: mobile ? "flex-start" : "center",
          justifyContent: "space-between",
          flexDirection: mobile ? "column" : "row",
          gap: mobile ? 4 : 0,
        }}
      >
        <span style={{ color:BRAND.white, fontSize:11, letterSpacing:2, fontFamily:font, textTransform:"uppercase" }}>
          {view==="log"&&"New Action Report"}
          {view==="list"&&"Action Records"}
          {view==="admin"&&"Admin Dashboard"}
        </span>
        <span style={{ color:BRAND.lightGray, fontSize:11, fontFamily:font }}>HR Module · Internal Use Only</span>
      </div>

      <div style={{ maxWidth:920, margin:"0 auto", padding: mobile ? "16px 12px" : "28px 16px" }}>

        {view==="log" && (
          <div>
            {submitted && (
              <div style={{ background:"#f2f4ee", border:`1px solid ${BRAND.green}`, borderRadius:8, padding:"14px 18px", marginBottom:20, color:BRAND.green, fontWeight:600, fontFamily:font }}>
                ✅ Action logged successfully and notification sent!
              </div>
            )}

            <div style={{ background:BRAND.white, borderRadius:10, border:`1px solid ${BRAND.lightGray}`, padding: mobile ? 16 : 28, boxShadow:"0 2px 8px rgba(0,0,0,0.06)", overflow:"hidden" }}>

              <div style={{ ...grid(mobile ? 1 : 3), marginBottom:16 }}>
                <Field label="Date *" type="date" value={form.date} onChange={v=>setForm({...form,date:v})} />
                <Field label="Employee Name *" placeholder="Full name" value={form.employeeName} onChange={v=>setForm({...form,employeeName:v})} />
                <Field label="Employee ID" placeholder="Aspire ID" value={form.employeeId} onChange={v=>setForm({...form,employeeId:v})} />
              </div>

              <div style={{ ...grid(mobile ? 1 : 2), marginBottom:20 }}>
                <Field label="Supervisor *" value={form.supervisor} onChange={v=>setForm({...form,supervisor:v})} />
                <Field label="Division / Crew" placeholder="e.g. Maintenance" value={form.division} onChange={v=>setForm({...form,division:v})} />
                <Field label="Jobsite / Location" placeholder="Property or address" value={form.jobsite} onChange={v=>setForm({...form,jobsite:v})} />
                <Field label="Aspire Ticket / Job #" value={form.aspireTicket} onChange={v=>setForm({...form,aspireTicket:v})} />
              </div>

              <div style={{ borderTop:`2px solid ${BRAND.lightGray}`, marginBottom:20 }} />

              <div style={{ marginBottom:18 }}>
                <label style={{ display:"block", fontSize:13, fontWeight:700, color:BRAND.black, marginBottom:10, fontFamily:font, textTransform:"uppercase", letterSpacing:1 }}>
                  Action Category *
                </label>
                <div style={grid(mobile ? 2 : 4, 8)}>
                  {Object.entries(INCIDENT_TYPES).map(([k,v])=>(
                    <button
                      key={k}
                      onClick={()=>setForm({...form,category:k,subType:""})}
                      style={{
                        padding:"11px 8px",
                        borderRadius:7,
                        border:`2px solid ${form.category===k ? v.color : BRAND.lightGray}`,
                        background:form.category===k ? v.bg : "#fafaf8",
                        cursor:"pointer",
                        textAlign:"center",
                        transition:"all 0.15s",
                        fontFamily:font,
                        minWidth:0,
                      }}
                    >
                      <div style={{ fontWeight:800, fontSize:13, color:v.color }}>{k}</div>
                      <div style={{ fontSize:10, color:BRAND.gray, marginTop:2 }}>{v.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {form.category && (
                <div style={{ marginBottom:18 }}>
                  <label style={{ display:"block", fontSize:13, fontWeight:700, color:BRAND.black, marginBottom:10, fontFamily:font, textTransform:"uppercase", letterSpacing:1 }}>
                    Action Type *
                  </label>
                  <div style={grid(mobile ? 1 : 2, 8)}>
                    {INCIDENT_TYPES[form.category].items.map(item=>(
                      <button
                        key={item}
                        onClick={()=>setForm({...form,subType:item})}
                        style={{
                          padding:"9px 14px",
                          borderRadius:6,
                          border:`1.5px solid ${form.subType===item ? INCIDENT_TYPES[form.category].color : BRAND.lightGray}`,
                          background:form.subType===item ? INCIDENT_TYPES[form.category].badge : BRAND.white,
                          cursor:"pointer",
                          textAlign:"left",
                          fontSize:13,
                          color:form.subType===item ? INCIDENT_TYPES[form.category].color : BRAND.black,
                          fontWeight:form.subType===item ? 700 : 400,
                          fontFamily:font,
                          minWidth:0,
                        }}
                      >
                        <span style={{ marginRight:8 }}>{form.subType===item ? "●" : "○"}</span>
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ borderTop:`2px solid ${BRAND.lightGray}`, marginBottom:18 }} />

              <div style={{ ...grid(mobile ? 1 : 2), marginBottom:16 }}>
                <div style={{ minWidth: 0 }}>
                  <label style={{ display:"block", fontSize:13, fontWeight:600, color:BRAND.black, marginBottom:6, fontFamily:font }}>Severity</label>
                  <select value={form.severity} onChange={e=>setForm({...form,severity:e.target.value})} style={sel}>
                    <option value="">Select...</option>
                    {SEVERITY.map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>

                <div style={{ minWidth: 0 }}>
                  <label style={{ display:"block", fontSize:13, fontWeight:600, color:BRAND.black, marginBottom:6, fontFamily:font }}>Action Taken</label>
                  <select value={form.actionTaken} onChange={e=>setForm({...form,actionTaken:e.target.value})} style={sel}>
                    <option value="">Select...</option>
                    {ACTION_TAKEN.map(a=><option key={a}>{a}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom:16 }}>
                <label style={{ display:"block", fontSize:13, fontWeight:600, color:BRAND.black, marginBottom:6, fontFamily:font }}>
                  Description / Notes *
                </label>
                <textarea
                  value={form.description}
                  onChange={e=>setForm({...form,description:e.target.value})}
                  placeholder="Describe what happened, where, and any relevant context..."
                  rows={4}
                  style={{ ...sel, resize:"vertical" }}
                />
              </div>

              <div style={{ ...grid(mobile ? 1 : 2), marginBottom:18 }}>
                <Field label="Witnesses" placeholder="Names (optional)" value={form.witnesses} onChange={v=>setForm({...form,witnesses:v})} />
                <div><label style={{ display:"block", fontSize:13, fontWeight:600, color:BRAND.black, marginBottom:6, fontFamily:font }}>Follow-Up Date</label><input type="date" value={form.followUpDate} onChange={e=>setForm({...form,followUpDate:e.target.value})} style={{...sel, height:"38px"}} /></div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={saving}
                style={{
                  width:"100%",
                  padding:"15px",
                  borderRadius:8,
                  border:"none",
                  background:saving ? BRAND.gray : BRAND.black,
                  color:BRAND.white,
                  fontWeight:700,
                  fontSize:15,
                  cursor:saving ? "not-allowed" : "pointer",
                  fontFamily:font,
                  letterSpacing:1,
                  textTransform:"uppercase",
                  transition:"all 0.2s"
                }}
              >
                {saving ? "Saving..." : "Submit Action Report →"}
              </button>
            </div>
          </div>
        )}

        {view==="list" && (
          <div>
            <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
              <input
                placeholder="Search employee or code..."
                value={search}
                onChange={e=>setSearch(e.target.value)}
                style={{ ...sel, flex:1, minWidth:200 }}
              />
              <select value={filterCat} onChange={e=>setFilterCat(e.target.value)} style={{ ...sel, width: mobile ? "100%" : "auto" }}>
                <option value="ALL">All Categories</option>
                {Object.entries(INCIDENT_TYPES).map(([k,v])=>(
                  <option key={k} value={k}>{k} – {v.label}</option>
                ))}
              </select>
              <button onClick={loadActions} style={{ padding:"9px 16px", borderRadius:6, border:`1px solid ${BRAND.lightGray}`, background:BRAND.white, fontSize:13, cursor:"pointer", fontFamily:font }}>
                ↻ Refresh
              </button>
            </div>

            {loading ? (
              <div style={{ textAlign:"center", padding:"60px 0", color:BRAND.gray, fontFamily:font }}>Loading records...</div>
            ) : filtered.length===0 ? (
              <div style={{ textAlign:"center", padding:"60px 0", color:BRAND.gray, fontFamily:font }}>No actions found.</div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {filtered.map(inc=>{
                  const cat = INCIDENT_TYPES[inc.category];
                  return (
                    <div
                      key={inc.firestoreId}
                      style={{
                        background:BRAND.white,
                        borderRadius:9,
                        border:`1px solid ${cat.border}`,
                        padding:"14px 18px",
                        display:"flex",
                        flexDirection: mobile ? "column" : "row",
                        gap:14,
                        alignItems: mobile ? "stretch" : "flex-start",
                        boxShadow:"0 1px 4px rgba(0,0,0,0.05)"
                      }}
                    >
                      <div style={{ background:cat.badge, borderRadius:6, padding:"6px 10px", fontWeight:800, fontSize:12, color:cat.color, whiteSpace:"nowrap", fontFamily:font, alignSelf: mobile ? "flex-start" : "auto" }}>
                        {inc.category}
                      </div>

                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:4 }}>
                          <span style={{ fontWeight:700, fontSize:14, fontFamily:font }}>{inc.employeeName}</span>
                          <span style={{ fontSize:12, color:BRAND.gray, fontFamily:font }}>{inc.date}</span>
                        </div>

                        <div style={{ fontSize:13, color:cat.color, fontWeight:600, marginTop:2, fontFamily:font }}>
                          {inc.code}
                        </div>

                        <div style={{ fontSize:12, color:BRAND.gray, marginTop:4, lineHeight:1.5, fontFamily:font }}>
                          {inc.description?.slice(0,120)}{inc.description?.length>120?"...":""}
                        </div>

                        <div style={{ display:"flex", gap:8, marginTop:8, flexWrap:"wrap", alignItems:"center" }}>
                          {inc.severity && <Tag label={inc.severity} />}
                          {inc.actionTaken && <Tag label={inc.actionTaken} />}
                          {inc.division && <Tag label={inc.division} />}
                          {inc.aspireTicket && <Tag label={"Aspire: " + inc.aspireTicket} />}

                          <div style={{ marginLeft: mobile ? 0 : "auto", display:"flex", gap:6, flexWrap:"wrap", width: mobile ? "100%" : "auto", marginTop: mobile ? 6 : 0 }}>
                            <button onClick={()=>setPrintInc(inc)} style={{ padding:"4px 10px", borderRadius:5, border:`1px solid ${BRAND.lightGray}`, background:"#f5f5f3", fontSize:11, cursor:"pointer", fontWeight:600, fontFamily:font }}>
                              🖨 Print
                            </button>

                            {isAdmin && (
                              <>
                                <button onClick={()=>setEditInc(inc)} style={{ padding:"4px 10px", borderRadius:5, border:"1px solid #b8d4ed", background:"#f0f6fb", color:"#1a4a6e", fontSize:11, cursor:"pointer", fontWeight:600, fontFamily:font }}>
                                  ✏️ Edit
                                </button>
                                <button onClick={()=>setDeleteId(inc.firestoreId)} style={{ padding:"4px 10px", borderRadius:5, border:"1px solid #f5c0c0", background:"#fdf2f2", color:"#7a1e1e", fontSize:11, cursor:"pointer", fontWeight:600, fontFamily:font }}>
                                  🗑 Delete
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {view==="admin" && (
          !isAdmin ? <AdminLogin onLogin={()=>setIsAdmin(true)} /> : (
            <div>
              <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginBottom:16, flexWrap:"wrap" }}>
                <button onClick={()=>{setIsAdmin(false);setView("log");}} style={{ padding:"7px 14px", borderRadius:6, border:`1px solid ${BRAND.lightGray}`, background:BRAND.white, fontSize:13, cursor:"pointer", color:"#7a1e1e", fontWeight:600, fontFamily:font }}>
                  🔒 Lock Admin
                </button>
                <button onClick={()=>setView("list")} style={{ padding:"7px 14px", borderRadius:6, border:`1px solid ${BRAND.lightGray}`, background:view==="list"?BRAND.green:BRAND.white, color:view==="list"?BRAND.white:BRAND.black, fontSize:13, cursor:"pointer", fontWeight:600, fontFamily:font }}>
                  ☰ Records
                </button>
              </div>

              <div style={{ background:ratioGood?"#f2f4ee":"#fdf8ee", border:`2px solid ${ratioGood?BRAND.green:"#c8a840"}`, borderRadius:12, padding:"20px 24px", marginBottom:20, display:"flex", gap:24, alignItems:"center", flexWrap:"wrap" }}>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:48, fontWeight:900, color:ratioGood?BRAND.green:"#7a5a1e", fontFamily:font }}>{ratio}:1</div>
                  <div style={{ fontSize:13, color:BRAND.gray, fontFamily:font }}>REC : Corrective Ratio</div>
                </div>

                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:15, color:ratioGood?BRAND.green:"#7a5a1e", marginBottom:4, fontFamily:font }}>
                    {ratioGood?"✅ Healthy Culture Ratio":"⚠️ Below Target – Log More Recognitions"}
                  </div>
                  <div style={{ fontSize:13, color:BRAND.gray, fontFamily:font }}>
                    Target: <strong>2:1</strong>. You have <strong>{recCount}</strong> recognitions and <strong>{corrCount}</strong> corrective actions.
                  </div>
                  {!ratioGood && corrCount>0 && (
                    <div style={{ fontSize:13, color:"#7a5a1e", marginTop:6, fontFamily:font }}>
                      Need <strong>{Math.max(0,corrCount*2-recCount)}</strong> more recognitions to hit 2:1.
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:12, marginBottom:20 }}>
                {Object.entries(INCIDENT_TYPES).map(([k,v])=>(
                  <div key={k} style={{ background:v.bg, border:`1px solid ${v.border}`, borderRadius:9, padding:"14px 16px" }}>
                    <div style={{ fontWeight:800, fontSize:13, color:v.color, fontFamily:font }}>{k}</div>
                    <div style={{ fontSize:11, color:BRAND.gray, marginBottom:8, fontFamily:font }}>{v.label}</div>
                    <div style={{ fontSize:32, fontWeight:900, color:v.color, fontFamily:font }}>{catCounts[k]}</div>
                    <div style={{ background:v.border, borderRadius:4, height:5, marginTop:8 }}>
                      <div style={{ height:"100%", background:v.color, borderRadius:4, width:`${actions.length ? Math.min(100,(catCounts[k]/actions.length)*100) : 0}%` }} />
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background:BRAND.white, borderRadius:9, border:`1px solid ${BRAND.lightGray}`, padding:"14px 18px", fontSize:13, color:BRAND.gray, fontFamily:font }}>
                <strong style={{ color:BRAND.black }}>Total:</strong> {actions.length} &nbsp;|&nbsp;
                <strong style={{ color:BRAND.green }}>Recognition:</strong> {recCount} &nbsp;|&nbsp;
                <strong style={{ color:"#7a1e1e" }}>Corrective:</strong> {corrCount}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}